import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { HelpCircle } from 'lucide-react';

export default function FAQ() {
  const faqs = [
    {
      category: "Services & Tarification",
      questions: [
        {
          q: "Combien coûtent vos services de conciergerie Airbnb ?",
          a: "Notre tarification est de 22% de commission sur les revenus locatifs + 80 CHF par mois de frais de gestion. Ce tarif inclut l'ensemble des services : création d'annonce, photos professionnelles, pricing dynamique, gestion des réservations, ménages, check-in/check-out, maintenance et reporting mensuel."
        },
        {
          q: "Quels services sont inclus dans votre prestation ?",
          a: "Tous nos services sont inclus dans le tarif unique : création et optimisation de l'annonce avec photos professionnelles, pricing dynamique quotidien, gestion complète des réservations et communication avec les voyageurs, ménages professionnels entre chaque séjour, accueil et remise des clés, maintenance et interventions, reporting mensuel détaillé."
        },
        {
          q: "Y a-t-il un engagement de durée minimum ?",
          a: "Non, il n'y a aucun engagement de durée. Vous pouvez arrêter notre service quand vous le souhaitez avec un préavis d'un mois."
        }
      ]
    },
    {
      category: "Réglementation & Légal",
      questions: [
        {
          q: "La location saisonnière Airbnb est-elle autorisée en Suisse ?",
          a: "La réglementation varie selon les communes et les cantons. Dans la plupart des villes de Suisse romande, la location saisonnière est autorisée mais peut nécessiter une déclaration ou une autorisation. Nous vous accompagnons pour vérifier votre situation spécifique."
        },
        {
          q: "Dois-je déclarer mes revenus Airbnb aux impôts ?",
          a: "Oui, les revenus de location saisonnière doivent être déclarés aux autorités fiscales suisses. Nous vous fournissons un reporting mensuel détaillé qui facilite cette déclaration."
        },
        {
          q: "Mon règlement de copropriété (PPE) peut-il interdire la location Airbnb ?",
          a: "Oui, certains règlements de copropriété peuvent restreindre ou interdire la location saisonnière. Il est important de vérifier ce point avant de démarrer. Nous vous conseillons de consulter votre administrateur de PPE."
        }
      ]
    },
    {
      category: "Processus & Gestion",
      questions: [
        {
          q: "Comment se passe la mise en place du service ?",
          a: "Après votre demande d'estimation, nous planifions une visite de votre bien pour réaliser les photos professionnelles. Ensuite, nous créons votre annonce optimisée, mettons en place le pricing dynamique et votre bien est prêt à accueillir ses premiers voyageurs sous 7 jours."
        },
        {
          q: "Qui gère les ménages entre les séjours ?",
          a: "Nous travaillons avec des prestataires de ménage professionnels locaux. Chaque ménage est contrôlé selon nos standards de qualité. Le coût des ménages est à votre charge (généralement entre 80-150 CHF selon la taille du bien) et est déduit des revenus locatifs."
        },
        {
          q: "Comment sont fixés les tarifs de location ?",
          a: "Nous utilisons un système de pricing dynamique qui ajuste les tarifs quotidiennement en fonction de la demande locale, des événements, de la saisonnalité et de la concurrence. L'objectif est de maximiser vos revenus tout en maintenant un taux d'occupation optimal."
        },
        {
          q: "Que se passe-t-il en cas de dégâts ou de problème pendant un séjour ?",
          a: "Nous gérons tous les incidents pendant les séjours : intervention rapide avec notre réseau d'artisans locaux, gestion des réclamations auprès d'Airbnb, coordination avec votre assurance si nécessaire. Tous les voyageurs sont couverts par la garantie hôte d'Airbnb."
        }
      ]
    },
    {
      category: "Revenus & Performance",
      questions: [
        {
          q: "Quand suis-je payé de mes revenus locatifs ?",
          a: "Vous recevez vos revenus mensuellement, le 15 de chaque mois. Le paiement inclut tous les revenus du mois précédent, déduction faite de notre commission, des frais de ménage et des éventuels frais de maintenance."
        },
        {
          q: "Puis-je bloquer mon bien pour usage personnel ?",
          a: "Oui, vous pouvez bloquer votre bien quand vous le souhaitez via notre interface ou en nous contactant directement. Il vous suffit de nous prévenir à l'avance pour que nous bloquions le calendrier."
        },
        {
          q: "Dans quelles villes proposez-vous vos services ?",
          a: "Nous intervenons dans 13 villes de Suisse romande : Lausanne, Genève, Verbier, Montreux, Crans-Montana, Neuchâtel, Villars, Yverdon-les-Bains, Fribourg, Vevey, Sion, Nyon et Morges."
        }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="FAQ - Questions Fréquentes | Conciergerie Airbnb Suisse"
        description="Toutes les réponses à vos questions sur nos services de conciergerie Airbnb en Suisse romande : tarifs, réglementation, processus, revenus."
        canonical="https://homely-conciergerie.ch/faq"
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <HelpCircle className="h-16 w-16 mx-auto mb-6 text-[#8A9474]" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Questions Fréquentes
            </h1>
            <p className="text-xl text-gray-200">
              Toutes les réponses sur notre service de conciergerie Airbnb
            </p>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {faqs.map((category, catIdx) => (
              <div key={catIdx}>
                <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6 flex items-center gap-3">
                  <span className="text-[#8A9474]">•</span> {category.category}
                </h2>
                
                <div className="space-y-4">
                  {category.questions.map((faq, qIdx) => (
                    <Card key={qIdx} className="border-2 border-[#E5E2DB]">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-[#2B2B2B] mb-3">
                          {faq.q}
                        </h3>
                        <p className="text-[#4A4A4A] leading-relaxed">
                          {faq.a}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B2B2B] mb-6">
              Vous avez d'autres questions ?
            </h2>
            <p className="text-xl text-[#4A4A4A] mb-8">
              Notre équipe est à votre disposition pour répondre à toutes vos questions
            </p>
            <a 
              href="mailto:info@homely-solutions.com"
              className="inline-block bg-[#8A9474] hover:bg-[#7A8464] text-white px-10 py-4 text-lg font-semibold rounded-lg transition-colors"
            >
              Nous contacter
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
}
